import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ErrorResponse } from '@thingslog/repositories';
import { FC, useLayoutEffect, useRef } from 'react';
import { useAuth } from './AuthContext';

interface SessionAuthSetupProps {
  Axios: AxiosInstance;
  onRequest?: (request: AxiosRequestConfig) => AxiosRequestConfig;
  onErrorCode?: (statusCode: number, error: AxiosError<ErrorResponse>) => void;
}

/**
 * Session based counterpart of AuthSetup. There is no token to attach or refresh - the cookie
 * travels with every request, so a 401 simply means the session is gone and the user has to log in
 * again.
 */
export const SessionAuthSetup: FC<SessionAuthSetupProps> = ({
  Axios,
  onRequest,
  onErrorCode,
}: SessionAuthSetupProps) => {
  const { login } = useAuth();

  const onRequestRef = useRef(onRequest);
  const onErrorCodeRef = useRef(onErrorCode);
  const loginRef = useRef(login);

  onRequestRef.current = onRequest;
  onErrorCodeRef.current = onErrorCode;
  loginRef.current = login;

  // Layout effect: class components mounted in the same commit fire requests from
  // componentDidMount, which runs before a plain useEffect would have registered the interceptors.
  useLayoutEffect(() => {
    const requestInterceptor = Axios.interceptors.request.use((request: AxiosRequestConfig) => {
      // Only same-origin requests carry the session - a third party URL must not get cookies
      // forced onto it (credentialed CORS would reject the response outright).
      const isCrossOrigin =
        request.url !== undefined &&
        /^https?:\/\//i.test(request.url) &&
        !request.url.startsWith(window.location.origin);
      request.withCredentials = !isCrossOrigin;
      return onRequestRef.current?.(request) ?? request;
    });

    const responseInterceptor = Axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: AxiosError<ErrorResponse>) => {
        const statusCode = error.response?.status;

        if (statusCode) {
          onErrorCodeRef.current?.(statusCode, error);

          if (statusCode === 401) {
            loginRef.current();
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      Axios.interceptors.request.eject(requestInterceptor);
      Axios.interceptors.response.eject(responseInterceptor);
    };
  }, [Axios]);

  return null;
};
